import { useEffect, useRef } from 'react'
import { HiSparkles } from 'react-icons/hi2'
import { MdPerson, MdGroup } from 'react-icons/md'
import {
    InternalConversation,
    InternalMessage,
} from '@/lib/api/internal-chat/internal-chat-api'
import { TypingUser } from '@/store/slices/internalChatSlice'
import MessageBubble from './MessageBubble'

interface ChatWindowProps {
    conversation: InternalConversation
    messages: InternalMessage[]
    typingUsers: TypingUser[]
    currentUserId: number
    onReply: (message: InternalMessage) => void
    loading?: boolean
}

export default function ChatWindow({
    conversation,
    messages,
    typingUsers,
    currentUserId,
    onReply,
    loading,
}: ChatWindowProps) {
    const messagesEndRef = useRef<HTMLDivElement>(null)

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [messages])

    const getTitle = () => {
        if (conversation.type === 'AI_CHAT') {
            return 'Assistente IA'
        }

        if (conversation.type === 'BROADCAST_ALL') {
            return conversation.title || 'Chat Geral'
        }

        if (conversation.type === 'BROADCAST_TYPE') {
            return (
                conversation.title ||
                `Para ${getUserTypeLabel(conversation.targetUserType)}`
            )
        }

        if (conversation.participants) {
            const otherParticipant = conversation.participants.find(
                p => p.id !== currentUserId
            )
            return otherParticipant?.name || 'Conversa'
        }

        return 'Conversa'
    }

    const getUserTypeLabel = (type?: string) => {
        switch (type) {
            case 'PROFESSIONAL':
                return 'Profissionais'
            case 'EMPLOYEE':
                return 'Funcionários'
            case 'COMPANY':
                return 'Administradores'
            default:
                return 'Usuários'
        }
    }

    const getIcon = () => {
        switch (conversation.type) {
            case 'AI_CHAT':
                return <HiSparkles className="text-secondary" />
            case 'BROADCAST_ALL':
                return <MdGroup className="text-success" />
            case 'BROADCAST_TYPE':
                return <MdGroup className="text-info" />
            default:
                return <MdPerson className="text-secondary" />
        }
    }

    const getSubtitle = () => {
        if (conversation.type === 'AI_CHAT') {
            return 'Assistente virtual'
        }
        if (conversation.type === 'BROADCAST_ALL') {
            return 'Mensagem para todos da empresa'
        }
        if (conversation.type === 'BROADCAST_TYPE') {
            return `Mensagem para ${getUserTypeLabel(conversation.targetUserType)}`
        }
        if (conversation.participants) {
            const otherParticipant = conversation.participants.find(
                p => p.id !== currentUserId
            )
            return otherParticipant?.email || ''
        }
        return ''
    }

    // Filter typing users for this conversation (excluding self)
    const typingInConversation = typingUsers.filter(
        t => t.conversationId === conversation.id && t.userId !== currentUserId
    )

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <span className="loading loading-spinner loading-md"></span>
            </div>
        )
    }

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Chat header */}
            <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 bg-base-100">
                <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${conversation.type === 'AI_CHAT'
                        ? 'bg-secondary/20'
                        : conversation.type === 'BROADCAST_ALL'
                            ? 'bg-success/20'
                            : conversation.type === 'BROADCAST_TYPE'
                                ? 'bg-info/20'
                                : 'bg-secondary/20'
                        }`}
                >
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-medium truncate">{getTitle()}</h2>
                    <p className="text-xs text-base-content/50 truncate">
                        {getSubtitle()}
                    </p>
                </div>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 bg-base-50">
                {messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-base-content/50">
                        <p>Nenhuma mensagem ainda</p>
                        <p className="text-sm mt-1">
                            Seja o primeiro a enviar uma mensagem
                        </p>
                    </div>
                ) : (
                    <>
                        {messages.map(message => (
                            <MessageBubble
                                key={message.id}
                                message={message}
                                isOwn={
                                    !message.isAiMessage &&
                                    message.senderId === currentUserId
                                }
                                onReply={onReply}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}

                {/* Typing indicator */}
                {typingInConversation.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-base-content/50 mt-2">
                        <span className="loading loading-dots loading-xs"></span>
                        {typingInConversation.length === 1
                            ? `${typingInConversation[0].userName} esta digitando...`
                            : `${typingInConversation.length} pessoas estão digitando...`}
                    </div>
                )}
            </div>
        </div>
    )
}
